import WhatsAppConnect from '../../components/WhatsAppConnect';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Plug,
  MessageSquare,
  Calendar,
  Check,
  ExternalLink,
  AlertCircle,
  Loader2,
  Save,
  Phone
} from 'lucide-react';
import axios from 'axios';
import ConversationCopies from '../../components/ConversationCopies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntegrationCard = ({ title, description, icon: Icon, connected, children, color = 'blue' }) => {
  const colors = {
    blue: 'from-blue-500 to-cyan-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-pink-600'
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 bg-gradient-to-br ${colors[color]} rounded-xl flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                {title}
                {connected && (
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                    Conectado
                  </span>
                )}
              </CardTitle>
              <CardDescription className="text-gray-500">{description}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
};

const Integrations = () => {
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  // WhatsApp config
  const [whatsappConfig, setWhatsappConfig] = useState({
    phone_number: '',
    phone_number_id: '',
    access_token: ''
  });

  // Doctoralia config
  const [doctoraliaConfig, setDoctoraliaConfig] = useState({
    client_id: '',
    client_secret: '',
    facility_id: '',
    doctor_id: '',
    address_id: '',
    country: 'mx'
  });

  // Google Calendar config
  const [googleConfig, setGoogleConfig] = useState({
    client_id: '',
    client_secret: '',
    calendar_id: ''
  });

  // Cal.com per-clinic config
  const [calcomConfig, setCalcomConfig] = useState({
    api_key: '',
    username: '',
    event_slug: '15min'
  });
  const [calcomStatus, setCalcomStatus] = useState(null);
  const [googleError, setGoogleError] = useState('');

  useEffect(() => {
    loadClinic();
    loadCalcomStatus();
  }, []);

  const loadCalcomStatus = async () => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/clinic/${clinicId}/calcom/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalcomStatus(res.data);
      if (res.data.username) setCalcomConfig(prev => ({ ...prev, username: res.data.username }));
      if (res.data.event_slug) setCalcomConfig(prev => ({ ...prev, event_slug: res.data.event_slug }));
    } catch {}
  };

  const saveCalcom = async () => {
    setSaving(prev => ({ ...prev, calcom: true }));
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/clinic/${clinicId}/calcom/config?api_key=${encodeURIComponent(calcomConfig.api_key)}&username=${encodeURIComponent(calcomConfig.username)}&event_slug=${encodeURIComponent(calcomConfig.event_slug)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadCalcomStatus();
    } catch (error) {
      console.error('Error saving Cal.com config:', error);
    } finally {
      setSaving(prev => ({ ...prev, calcom: false }));
    }
  };

  const loadClinic = async () => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/clinic/${clinicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClinic(response.data);
      
      // Load existing configs
      if (response.data.whatsapp_integration) {
        setWhatsappConfig(prev => ({ ...prev, ...response.data.whatsapp_integration }));
      }
      if (response.data.calendar_integration?.credentials) {
        if (response.data.calendar_integration.provider === 'doctoralia') {
          setDoctoraliaConfig(prev => ({ ...prev, ...response.data.calendar_integration.credentials }));
        }
      }
    } catch (error) {
      console.error('Error loading clinic:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveWhatsApp = async () => {
    setSaving(prev => ({ ...prev, whatsapp: true }));
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      await axios.patch(`${API}/clinic/${clinicId}`, {
        whatsapp_integration: {
          ...whatsappConfig,
          is_connected: !!(whatsappConfig.phone_number_id && whatsappConfig.access_token)
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadClinic();
    } catch (error) {
      console.error('Error saving WhatsApp config:', error);
    } finally {
      setSaving(prev => ({ ...prev, whatsapp: false }));
    }
  };

  const saveDoctoralia = async () => {
    setSaving(prev => ({ ...prev, doctoralia: true }));
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      await axios.patch(`${API}/clinic/${clinicId}`, {
        calendar_integration: {
          provider: 'doctoralia',
          is_connected: !!(doctoraliaConfig.client_id && doctoraliaConfig.client_secret),
          credentials: doctoraliaConfig,
          doctoralia_facility_id: doctoraliaConfig.facility_id,
          doctoralia_doctor_id: doctoraliaConfig.doctor_id,
          doctoralia_address_id: doctoraliaConfig.address_id
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadClinic();
    } catch (error) {
      console.error('Error saving Doctoralia config:', error);
    } finally {
      setSaving(prev => ({ ...prev, doctoralia: false }));
    }
  };

  const saveGoogleCalendar = async () => {
    setSaving(prev => ({ ...prev, google: true }));
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      await axios.patch(`${API}/clinic/${clinicId}`, {
        calendar_integration: {
          provider: 'google',
          is_connected: !!(googleConfig.client_id && googleConfig.client_secret),
          credentials: googleConfig,
          external_id: googleConfig.calendar_id || 'primary'
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadClinic();
    } catch (error) {
      console.error('Error saving Google Calendar config:', error);
    } finally {
      setSaving(prev => ({ ...prev, google: false }));
    }
  };

  const connectGoogleCalendar = async () => {
    setSaving(prev => ({ ...prev, google: true }));
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/auth/google/authorize/${clinicId}?redirect=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      setSaving(prev => ({ ...prev, google: false }));
    }
  };

  const disconnectGoogleCalendar = async () => {
    setSaving(prev => ({ ...prev, google: true }));
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      await axios.patch(`${API}/clinic/${clinicId}`, {
        calendar_integration: {
          provider: 'google',
          is_connected: false,
          credentials: null
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadClinic();
    } catch (error) {
      console.error('Error disconnecting:', error);
    } finally {
      setSaving(prev => ({ ...prev, google: false }));
    }
  };

  // Check for OAuth callback success/error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');
    
    if (success === 'google_connected') {
      loadClinic();
      // Clear URL params
      window.history.replaceState({}, '', '/dashboard/integrations');
    }
    if (error) {
      console.error('OAuth error:', error);
      setGoogleError(error === 'access_denied'
        ? 'No se autorizó el acceso a Google Calendar.'
        : `No se pudo conectar Google Calendar: ${error}`);
      window.history.replaceState({}, '', '/dashboard/integrations');
    }
  }, []);

  const countries = [
    { code: 'mx', name: 'México', domain: 'doctoralia.mx' },
    { code: 'es', name: 'España', domain: 'doctoralia.es' },
    { code: 'ar', name: 'Argentina', domain: 'doctoraliar.com' },
    { code: 'br', name: 'Brasil', domain: 'doctoralia.com.br' },
    { code: 'cl', name: 'Chile', domain: 'doctoralia.cl' },
    { code: 'co', name: 'Colombia', domain: 'doctoralia.co' },
    { code: 'pe', name: 'Perú', domain: 'doctoralia.pe' },
    { code: 'it', name: 'Italia', domain: 'miodottore.it' },
    { code: 'pl', name: 'Polonia', domain: 'znanylekarz.pl' },
    { code: 'de', name: 'Alemania', domain: 'jameda.de' }
  ];

  if (loading) {
    return (
      <DashboardLayout title="Integraciones">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Integraciones">
      <div className="space-y-6">
        <ConversationCopies />
        <section className="rounded-xl border border-gray-700 bg-slate-900 p-6">
          <h2 className="mb-3 text-lg font-semibold text-white">Conectar WhatsApp con Meta</h2>
          <WhatsAppConnect  />
        </section>
        {/* WhatsApp */}
        <IntegrationCard
          title="WhatsApp Business"
          description="Recibe mensajes de pacientes y leads de Meta Ads, TikTok y Google"
          icon={MessageSquare}
          color="green"
          connected={clinic?.whatsapp_integration?.is_connected}
        >
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                <div className="text-sm">
                  <p className="text-blue-400 font-medium">Cómo obtener las credenciales:</p>
                  <ol className="text-blue-300/80 mt-2 space-y-1 list-decimal list-inside">
                    <li>Ve a <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="underline">business.facebook.com</a></li>
                    <li>Crea o selecciona tu cuenta de negocio</li>
                    <li>Agrega WhatsApp y verifica tu número</li>
                    <li>En developers.facebook.com, crea una app de tipo "Business"</li>
                    <li>Copia el Phone Number ID y genera un Access Token permanente</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Número de teléfono</Label>
                <Input
                  value={whatsappConfig.phone_number}
                  onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_number: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400">Phone Number ID</Label>
                <Input
                  value={whatsappConfig.phone_number_id}
                  onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_number_id: e.target.value })}
                  placeholder="1234567890"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Access Token</Label>
              <Input
                type="password"
                value={whatsappConfig.access_token}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, access_token: e.target.value })}
                placeholder="EAAxxxxxxx..."
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <Button
              onClick={saveWhatsApp}
              disabled={saving.whatsapp}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving.whatsapp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar configuración
            </Button>
          </div>
        </IntegrationCard>

        {/* Google Calendar - Principal */}
        <IntegrationCard
          title="Google Calendar"
          description="Sistema de citas recomendado - sincroniza con tu calendario de Google"
          icon={Calendar}
          color="purple"
          connected={clinic?.calendar_integration?.provider === 'google' && clinic?.calendar_integration?.is_connected}
        >
          <div className="space-y-4">
            {googleError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3" role="alert">
                <p className="text-red-400 text-sm">{googleError}</p>
              </div>
            )}
            {clinic?.calendar_integration?.provider === 'google' && clinic?.calendar_integration?.is_connected ? (
              // Connected state
              <>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-green-400 font-medium">¡Google Calendar conectado!</p>
                      <p className="text-green-300/80 mt-1">
                        Tu calendario está sincronizado. Las citas se crearán automáticamente.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => window.open('https://calendar.google.com', '_blank')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ver Google Calendar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={disconnectGoogleCalendar}
                    disabled={saving.google}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    {saving.google ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Desconectar
                  </Button>
                </div>
              </>
            ) : (
              // Not connected state
              <>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-green-400 font-medium">Opción recomendada</p>
                      <ul className="text-green-300/80 mt-2 space-y-1 list-disc list-inside">
                        <li>Sincronización automática de citas</li>
                        <li>Notificaciones y recordatorios</li>
                        <li>Compatible con todos tus dispositivos</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-blue-400 font-medium">Un click para conectar</p>
                      <p className="text-blue-300/80 mt-1">
                        Haz click en el botón de abajo para autorizar acceso a tu Google Calendar.
                        Se conectará con la cuenta de Google que selecciones al momento de autorizar.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={connectGoogleCalendar}
                  disabled={saving.google}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6"
                >
                  {saving.google ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Calendar className="w-5 h-5 mr-2" />
                  )}
                  Conectar Google Calendar
                </Button>
              </>
            )}
          </div>
        </IntegrationCard>

        {/* Doctoralia - No disponible */}
        <Card className="bg-white/5 border-white/10 opacity-60">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-gray-400 flex items-center gap-2">
                    Doctoralia
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                      No disponible
                    </span>
                  </CardTitle>
                  <CardDescription className="text-gray-500">API no disponible públicamente</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
              <p className="text-yellow-300/80 text-sm">
                Doctoralia no ofrece API pública para integraciones externas. Recomendamos usar <strong>Google Calendar</strong> como alternativa.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cal.com */}
        <IntegrationCard
          title="Cal.com"
          description="Calendario alternativo — se usa cuando Google Calendar no está conectado"
          icon={Calendar}
          color="blue"
          connected={calcomStatus?.source === 'clinic'}
        >
          <div className="space-y-4">
            {calcomStatus && (
              <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${
                calcomStatus.source === 'clinic'
                  ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                  : calcomStatus.source === 'global'
                  ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400'
                  : 'bg-gray-500/10 border border-gray-500/20 text-gray-400'
              }`}>
                {calcomStatus.source === 'clinic' && <Check className="w-4 h-4" />}
                {calcomStatus.source === 'clinic'
                  ? `Configurado para esta clínica (${calcomStatus.username})`
                  : calcomStatus.source === 'global'
                  ? 'Usando cuenta global del sistema'
                  : 'No configurado'}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">API Key de Cal.com</Label>
                <Input
                  type="password"
                  value={calcomConfig.api_key}
                  onChange={e => setCalcomConfig({ ...calcomConfig, api_key: e.target.value })}
                  placeholder="cal_live_xxxxxxxxxxxx"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400">Username de Cal.com</Label>
                <Input
                  value={calcomConfig.username}
                  onChange={e => setCalcomConfig({ ...calcomConfig, username: e.target.value })}
                  placeholder="mi-clinica"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Slug del evento (duración)</Label>
              <Input
                value={calcomConfig.event_slug}
                onChange={e => setCalcomConfig({ ...calcomConfig, event_slug: e.target.value })}
                placeholder="15min"
                className="bg-white/5 border-white/10 text-white mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Ejemplo: 15min, 30min, consulta</p>
            </div>
            <Button
              onClick={saveCalcom}
              disabled={saving.calcom || !calcomConfig.username}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving.calcom ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar configuración Cal.com
            </Button>
          </div>
        </IntegrationCard>

        {/* Contact for other integrations */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-6">
            <p className="text-gray-400 text-center">
              ¿Necesitas otra integración? <a href="mailto:soporte@aimedplanner.com" className="text-blue-400 hover:underline">Contáctanos</a>.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Integrations;
